import { Document, PDFDownloadLink, StyleSheet, View } from '@react-pdf/renderer';
import { TFunction } from 'i18next';
import { CloudDownloadOutlined } from '@ant-design/icons';

import ReportCoverPage from '@/components/Shared/Reports/ReportCoverPage';
import ReportSectionHeader from '@/components/Shared/Reports/components/ReportSectionHeader';
import ReportsTable, { ReportsTableConfig } from '@/components/Shared/Reports/components/ReportsTable';
import ReportTemplatePage, { ReportHeaderInfo } from '@/components/Shared/Reports/ReportTemplatePage';
import { StyledButton } from '@/lib/theme/components/Button';
import themeToken from '@/lib/theme/tokens';

import { VoteResultType } from '../types';
import { useGetVoteResults } from '../api/getVotingResults';
import { useGetCompany } from '@/features/company/api/getCompany';

type VotingResultsReportPdfProps = {
  voteResults: VoteResultType[];
  reportHeaderInfo: ReportHeaderInfo;
  t: TFunction<'vote', 'vote'>;
  currentLanguage: string;
  meetingName: string;
  companyLogo?: string;
};

type VotingResultsReportDownloadProps = {
  companyID: number;
  meetingID?: number;
  reportHeaderInfo: ReportHeaderInfo;
  t: TFunction<'vote', 'vote'>;
  currentLanguage: string;
  meetingName: string;
};

const rowStyles = StyleSheet.create({
  tableHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTopRightRadius: 6,
    borderTopLeftRadius: 6,
    fontSize: 8,
    fontWeight: 'bold',
    color: '#271060',
    backgroundColor: '#EDEFF4',
  },
  tableRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 8,
    fontSize: 8,
    color: '#271060',
  },
  tableRowWithBg: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 8,
    fontSize: 8,
    color: '#271060',
    backgroundColor: '#F6F7FA',
  },
});

const VotingResultsReportPdf = ({
  voteResults,
  reportHeaderInfo,
  t,
  currentLanguage,
  meetingName,
  companyLogo,
}: VotingResultsReportPdfProps) => {
  const columns: ReportsTableConfig[] = [
    {
      key: 'clause',
      title: t('clause'),
      dataSourceKey: 'clause',
      width: '10%',
    },
    {
      key: 'points_description',
      title: t('question'),
      dataSourceKey: 'points_description',
      width: '50%',
      textAlign: currentLanguage === 'ar' ? 'right' : 'left',
    },
    ...[
      { key: 'yes', title: t('yes') },
      { key: 'no', title: t('no') },
      { key: 'no_vote', title: t('noVote') },
      { key: 'excluded', title: t('excluded') },
    ].map(({ key, title }) => ({
      key,
      title,
      dataSourceKey: key,
      width: '10%',
      textAlign: 'center' as const,
    })),
  ];

  const getRows = (metric: 'count' | 'percentage') =>
    voteResults.map(({ id, clause, points_description, results }) => ({
      id,
      clause,
      points_description,
      yes:
        metric === 'count'
          ? (results?.[0]?.count ?? 0).toLocaleString()
          : `${Number(results?.[0]?.percentage ?? 0).toFixed(4)}%`,
      no:
        metric === 'count'
          ? (results?.[1]?.count ?? 0).toLocaleString()
          : `${Number(results?.[1]?.percentage ?? 0).toFixed(4)}%`,
      no_vote:
        metric === 'count'
          ? (results?.[2]?.count ?? 0).toLocaleString()
          : `${Number(results?.[2]?.percentage ?? 0).toFixed(4)}%`,
      excluded:
        metric === 'count'
          ? (results?.[3]?.count ?? 0).toLocaleString()
          : `${Number(results?.[3]?.percentage ?? 0).toFixed(4)}%`,
    }));

  const tableConfig = currentLanguage === 'ar' ? [...columns].reverse() : columns;

  return (
    <Document>
      <ReportCoverPage reportName={meetingName} companyLogo={companyLogo} reportNameAddtion={t('results')} />
      <ReportTemplatePage reportName={t('results')} reportHeaderInfo={reportHeaderInfo} hasCurrency={false}>
        <ReportSectionHeader title={t('voteResults')} currentLanguage={currentLanguage} />
        <View style={{ marginTop: 8, marginBottom: 16 }}>
          <View style={{ marginBottom: 8 }}>
            <ReportSectionHeader title={t('totalShares')} currentLanguage={currentLanguage} />
          </View>
          <ReportsTable
            tableConfig={tableConfig}
            dataSource={getRows('count')}
            rowStyle={rowStyles.tableRow}
            rowStyleWithBg={rowStyles.tableRowWithBg}
            tableHeaderStyle={rowStyles.tableHeader}
          />
        </View>
      </ReportTemplatePage>
      <ReportTemplatePage reportName={t('results')} reportHeaderInfo={reportHeaderInfo} hasCurrency={false}>
        <View style={{ marginTop: 8, marginBottom: 8 }}>
          <ReportSectionHeader title={t('ownership')} currentLanguage={currentLanguage} />
        </View>
        <ReportsTable
          tableConfig={tableConfig}
          dataSource={getRows('percentage')}
          rowStyle={rowStyles.tableRow}
          rowStyleWithBg={rowStyles.tableRowWithBg}
          tableHeaderStyle={rowStyles.tableHeader}
        />
      </ReportTemplatePage>
    </Document>
  );
};

const VotingResultsReportDownload = ({
  companyID,
  meetingID,
  reportHeaderInfo,
  t,
  currentLanguage,
  meetingName,
}: VotingResultsReportDownloadProps) => {
  const {
    data: ownershipResults,
    dataUpdatedAt: ownershipResultsUpdatedAt,
    isPending: isOwnershipResultsLoading,
  } = useGetVoteResults({
    companyID,
    meetingID,
    isByOwnership: true,
  });

  const { data: companyData } = useGetCompany({
    companyID: companyID,
  });

  return ownershipResults?.data.length ? (
    <PDFDownloadLink
      key={`${currentLanguage}-${ownershipResultsUpdatedAt}`}
      document={
        <VotingResultsReportPdf
          meetingName={meetingName}
          companyLogo={companyData?.company_data.logo || ''}
          voteResults={ownershipResults.data}
          reportHeaderInfo={reportHeaderInfo}
          t={t}
          currentLanguage={currentLanguage}
        />
      }
      fileName="voting-results-by-ownership.pdf"
    >
      <StyledButton
        style={{ padding: `${themeToken.paddingXXS}px ${themeToken.paddingLG}px` }}
        size="small"
        type="primary"
        ghost
        disabled={isOwnershipResultsLoading}
        icon={<CloudDownloadOutlined />}
      >
        {t('download')}
      </StyledButton>
    </PDFDownloadLink>
  ) : (
    <StyledButton
      style={{ padding: `${themeToken.paddingXXS}px ${themeToken.paddingLG}px` }}
      size="small"
      type="primary"
      ghost
      loading={isOwnershipResultsLoading}
      disabled
      icon={<CloudDownloadOutlined />}
    >
      {t('download')}
    </StyledButton>
  );
};

export default VotingResultsReportDownload;
